import React, { useState, useEffect } from 'react';
import { Tag as TagIcon, Plus, Pencil, Trash2 } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import CustomAlert from './shared/CustomAlert';

// ⭐ Módulo propio del sidebar (pedido de Angelo, 2026-08-07: "saca las
// etiquetas de configuración y colócalo en el dashboard pos"). Antes vivía
// como un tab dentro de Configuracion.js (ver commit del mismo día que creó
// PUT /api/pos/crm/tags/:id) -- se sacó de ahí tal cual, mismo endpoint
// GET/POST/PUT/DELETE /api/pos/crm/tags que ya usan Conversaciones.js,
// PipelineManager.js y el nodo Condición de Workflows, así que cualquier
// cambio acá se sigue reflejando en los 3 lados sin tocarlos.
//
// Mismos swatches que los otros 3 lugares donde se crean/editan
// etiquetas -- consistencia visual.
const TAG_COLOR_SWATCHES = ['#3b82f6', '#8b5cf6', '#f59e0b', '#f97316', '#22c55e', '#ef4444', '#ec4899', '#6b7280'];

const EtiquetasManager = () => {
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLOR_SWATCHES[0]);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState(TAG_COLOR_SWATCHES[0]);
  const [customAlert, setCustomAlert] = useState(null);

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const res = await apiClient.get('/api/pos/crm/tags');
      setTags(res.data || []);
    } catch (error) {
      showAlert('error', 'Error', 'No se pudieron cargar las etiquetas');
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => { loadTags(); }, []);

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    try {
      await apiClient.post('/api/pos/crm/tags', { name, color: newTagColor });
      setNewTagName('');
      setNewTagColor(TAG_COLOR_SWATCHES[0]);
      setCreatingTag(false);
      loadTags();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo crear la etiqueta');
    }
  };

  const startEditTag = (tag) => {
    setEditingTagId(tag.id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color || TAG_COLOR_SWATCHES[0]);
  };

  const handleSaveEditTag = async () => {
    const name = editTagName.trim();
    if (!name) return;
    try {
      await apiClient.put(`/api/pos/crm/tags/${editingTagId}`, { name, color: editTagColor });
      setEditingTagId(null);
      loadTags();
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo actualizar la etiqueta');
    }
  };

  const confirmDeleteTag = (tag) => {
    setCustomAlert({
      type: 'confirm',
      title: '¿Eliminar esta etiqueta?',
      message: `"${tag.name}" se va a quitar de ${Number(tag.count) || 0} contacto${Number(tag.count) === 1 ? '' : 's'} que la tienen asignada. Esto no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await apiClient.delete(`/api/pos/crm/tags/${tag.id}`);
          loadTags();
        } catch (error) {
          showAlert('error', 'Error', error.response?.data?.error || 'No se pudo eliminar la etiqueta');
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
          onConfirm={customAlert.onConfirm}
          zIndex={50}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <TagIcon className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Etiquetas</h1>
            <p className="text-sm text-gray-600">
              Las etiquetas que crees o edites acá aparecen automáticamente en Conversaciones, Pipeline y en el nodo Condición de Workflows.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-5 space-y-6 max-w-2xl">
        {loadingTags ? (
          <p className="text-sm text-gray-400">Cargando...</p>
        ) : (
          <div className="space-y-2">
            {tags.length === 0 && (
              <p className="text-sm text-gray-400 italic">Todavía no hay ninguna etiqueta creada.</p>
            )}
            {tags.map(tag => (
              <div key={tag.id} className="border rounded-lg p-3">
                {editingTagId === tag.id ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      autoFocus
                      value={editTagName}
                      onChange={(e) => setEditTagName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditTag(); if (e.key === 'Escape') setEditingTagId(null); }}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                    <div className="flex gap-1.5">
                      {TAG_COLOR_SWATCHES.map(c => (
                        <button key={c} onClick={() => setEditTagColor(c)} style={{ backgroundColor: c }} className={`w-5 h-5 rounded-full ${editTagColor === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleSaveEditTag} className="flex-1 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold">Guardar</button>
                      <button onClick={() => setEditingTagId(null)} className="flex-1 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs font-semibold">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                      <span className="font-medium text-gray-800 truncate">{tag.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {Number(tag.count) || 0} {Number(tag.count) === 1 ? 'contacto' : 'contactos'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => startEditTag(tag)} className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => confirmDeleteTag(tag)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {creatingTag ? (
          <div className="border rounded-lg p-3 bg-gray-50">
            <input
              type="text"
              autoFocus
              placeholder="Nombre de la etiqueta..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); if (e.key === 'Escape') setCreatingTag(false); }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
            <div className="flex gap-1.5 mt-2">
              {TAG_COLOR_SWATCHES.map(c => (
                <button key={c} onClick={() => setNewTagColor(c)} style={{ backgroundColor: c }} className={`w-5 h-5 rounded-full ${newTagColor === c ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`} />
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={handleCreateTag} className="flex-1 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold">Crear</button>
              <button onClick={() => { setCreatingTag(false); setNewTagName(''); }} className="flex-1 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs font-semibold">Cancelar</button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setCreatingTag(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Nueva etiqueta
          </button>
        )}
      </div>
    </div>
  );
};

export default EtiquetasManager;
